import { BaseApi } from './base-api.model';
import { VersionedEntityId } from './document-reference.model';

/**
 * v2 deal / Item read model.
 *
 * Mirrors the backend v2 DTO `io.qtek.libv2.apis.ItemApi` — which itself
 * extends `BaseApi<Item>` for the audit/scope envelope
 *
 * Wire facts baked into these types:
 * - Enum-typed fields (`stage`, `status`, `currency`, `itemSide`,
 *   `ndaWorkflow`) serialize as the enum NAME string — e.g. `stage: "MANDATED"`,
 *   `currency: "USD"` — NOT the numeric stage/status code.
 * - The backend serializes with NON_EMPTY / NON_NULL inclusion, so empty
 *   strings, empty collections and nulls are dropped from the frame — every
 *   field is therefore optional here.
 */
export interface Item extends BaseApi {
  // --- ItemApi own fields ---
  sequenceNumber?: number;
  /** Owning company id (UUID). */
  company?: string;
  /** Parent deal id (UUID) — the V1 `pid`. Send null to clear the link. */
  parent?: string | null;
  /** Caller's own participation on this deal (DEAL_ADMIN on create). */
  myPar?: Participation;
  pars?: ItemData[];
  status?: ItemStatus;
  stage?: ItemStage;
  /** ISO-8601 string. */
  stageTimestamp?: string;
  ndaWorkflow?: ItemNdaWorkflow;
  /** UUID. */
  ndaTemplate?: string;
  /** Service-type bitmask — same values as V1 `prTp` (see {@link ItemTypeBitMasks}). */
  productType?: ItemTypeBitMask;
  name?: string;
  description?: string;
  currency?: Currency;
  folder?: string;
  imgs?: string[];
  externalVendorList?: ExternalVendor[];
  qty?: number;
  /** UUID. */
  docId?: string;
  /** Schema-driven form data — the V1 `data` wrapper is now `itemData`. */
  itemData?: ItemData;
  contacts?: ParticipationContact[];
  accounts?: Company[];
  /** Id + version of the pinned TemplateData row (nda-dialog needs both to fetch that version). */
  ndaData?: VersionedEntityId;
  templateStructureId?: string;
  participantId?: string;
  templateWorkflowIds?: string[];
  url?: string;
  itemSide?: ItemSide;
  /** UUID. */
  leadCompany?: string;
  /** UUID. */
  leadContactUser?: string;
  /** Wire key is `publishedToBenchmark` (Jackson strips the `is` prefix). */
  publishedToBenchmark?: boolean;
  subItems?: Item[];

  // --- client-only, never sent by the backend ---
  /** UI selection state (grid). */
  selected?: boolean;
  /** Derived on the client from {@link ItemData.shortIdMap} via `swapKeyValue`. */
  namespace?: { [key: string]: string };
}

/**
 * Deal stage. Serialized as the enum NAME, which is also the key the schema
 * type `io.synply.v1.type.stage` uses for its values.
 */
export enum ItemStage {
  UNKNOWN = 'UNKNOWN',
  PRE_MANDATE = 'PRE_MANDATE',
  MANDATED = 'MANDATED',
  UNDERWRITING = 'UNDERWRITING',
  CREDIT_APPROVAL = 'CREDIT_APPROVAL',
  SYNDICATION = 'SYNDICATION',
  COMMITTED = 'COMMITTED',
  CLOSING = 'CLOSING',
  CLOSED = 'CLOSED',
  AGENCY = 'AGENCY',
  PAID_OUT = 'PAID_OUT',
}

/**
 * Deal status. Serialized as the enum NAME, which is also the key the schema
 * type `io.synply.v1.type.status` uses for its values.
 */
export enum ItemStatus {
  UNDEFINED = 'UNDEFINED',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  LOST_TO_COMPETITOR = 'LOST_TO_COMPETITOR',
}

export enum ItemSide {
  PRIVATE_COPY = 'PRIVATE_COPY',
  OFFICIAL = 'OFFICIAL',
}

export enum ItemNdaWorkflow {
  NONE = 'NONE',
  INSTITUTION = 'INSTITUTION',
  PERSONAL = 'PERSONAL',
}

export enum Currency {
  USD = 'USD',
}

/**
 * Schema-driven form data (io.qtek.libv2.apis.ItemDataApi). The V1 `data`
 * wrapper is now `itemData`; V1 `sid` is now `shortIdMap`, V1 `data` stays `data`.
 */
export interface ItemData {
  _id?: string;
  title?: string;
  /** Short-id ↔ long-id map (V1 `sid`); fed to `swapKeyValue` to build the namespace. */
  shortIdMap?: { [key: string]: string };
  /** Schema key → value (e.g. `c.indu`, `c.pr`, `p.parInd`). Same keys as V1. */
  data?: { [key: string]: string };
}

/** Currency amount (io.qtek.libv2.apis.CurrencyAmount) — the V1 `{a, c}`. */
export interface CurrencyAmount {
  /** amount */
  a?: number;
  /** currency */
  c?: Currency | string;
}

export enum ParticipationRole {
  ACCESS_REQUESTED = 'ACCESS_REQUESTED',
  NO_ACCESS_NO_VIEW = 'NO_ACCESS_NO_VIEW',
  PENDING = 'PENDING',
  DEAL_ADMIN = 'DEAL_ADMIN',
  DOCUMENT_MANAGER = 'DOCUMENT_MANAGER',
  PARTICIPANT = 'PARTICIPANT',
  LEGAL = 'LEGAL',
  CPA = 'CPA',
}

export enum ParticipationStage {
  UNKNOWN = 'UNKNOWN',
  AWAITING_CONTACT_INVITATION_RESPONSE = 'AWAITING_CONTACT_INVITATION_RESPONSE',
  PENDING = 'PENDING',
  INTERESTED = 'INTERESTED',
  // Backend spelling is single-T (COMMITED).
  COMMITED = 'COMMITED',
  DELIVERED = 'DELIVERED',
  UNDERWRITING = 'UNDERWRITING',
  CREDIT_APPROVED = 'CREDIT_APPROVED',
  TRACKING = 'TRACKING',
  SETTLED = 'SETTLED',
  PASSED = 'PASSED',
}

/** Stages the backend sets on its own; a user never picks them. */
const BACKEND_ONLY_PARTICIPATION_STAGES: ReadonlySet<ParticipationStage> =
  new Set([
    ParticipationStage.UNKNOWN,
    ParticipationStage.AWAITING_CONTACT_INVITATION_RESPONSE,
    ParticipationStage.TRACKING,
  ]);

export const SELECTABLE_PARTICIPATION_STAGES: ReadonlyArray<
  [string, ParticipationStage]
> = (
  Object.entries(ParticipationStage) as [string, ParticipationStage][]
).filter(([, stage]) => !BACKEND_ONLY_PARTICIPATION_STAGES.has(stage));

/**
 * A participation on a deal (io.qtek.libv2.apis.ParticipationApi), scoped to the
 * fields the frontend reads/writes.
 *
 * On Item create, `myPar` seeds the caller's DEAL_ADMIN self-participation —
 * only `role` (must be `DEAL_ADMIN`) and the optional
 * `purchasePrice`/`upFee`/`servicing` are consumed; commitment amounts are NOT
 * read on create and must be set via a separate `Participation` write.
 */
export interface Participation {
  _id?: string;
  /** UUID of the deal. */
  item?: string;
  /** UUID. */
  participantCompany?: string;
  /** UUID. */
  leadCompany?: string;
  email?: string;
  role?: ParticipationRole;
  participationStage?: ParticipationStage;
  pr?: CurrencyAmount;
  indicatedQuantity?: CurrencyAmount;
  committedQuantity?: CurrencyAmount;
  allocatedQuantity?: CurrencyAmount;
  balanceQuantity?: CurrencyAmount;
  purchasePrice?: number;
  upFee?: number;
  servicing?: number;
  currency?: string;
}

export enum ParticipationContactStage {
  UNKNOWN = 'UNKNOWN',
  REQUIRES_APPROVAL = 'REQUIRES_APPROVAL',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
}

export enum ParticipationContactRole {
  SYNDICATIONS = 'SYNDICATIONS',
  UNDERWRITER = 'UNDERWRITER',
  LOAN_OFFICER = 'LOAN_OFFICER',
  RELATIONSHIP_MANAGER = 'RELATIONSHIP_MANAGER',
  CREDIT = 'CREDIT',
  CLOSING = 'CLOSING',
  OPERATIONS = 'OPERATIONS',
}

/**
 * A contact on a participation (io.qtek.libv2.apis.ParticipationContactApi),
 * scoped to the fields the frontend consumes. Replaces the V1 inline
 * `ItemContact` shape.
 */
export interface ParticipationContact {
  _id?: string;
  /** UUID. */
  participation?: string;
  /** UUID. */
  user?: string;
  email?: string;
  name?: string;
  stage?: ParticipationContactStage;
  primaryContact?: boolean;
  role?: ParticipationContactRole;
  areaOfResponsibility?: ParticipationContactRole;
  /** UUID. */
  companyId?: string;
  contactUser?: {
    _id?: string;
    email?: string;
    person?: {
      firstName?: string;
      lastName?: string;
      avatarDocId?: string;
      workPhone?: ContactPhone;
      cellPhone?: ContactPhone;
    };
    company?: {
      _id?: string;
      companyName?: string;
      logoImage?: string;
    };
  };
}

export interface ContactPhone {
  number?: string;
  countryCode?: string;
  dialCode?: string;
  /** WORK=200, MOBILE=100. */
  type?: number;
}

/** External vendor reference (io.qtek.libv2.apis.ExternalVendorApi). */
export interface ExternalVendor {
  externalVendorId?: string;
  externalVendorName?: string;
  vendorType?: string;
  /** UUID. */
  companyId?: string;
  params?: { [key: string]: string };
}

/**
 * Minimal company reference as embedded in {@link Item.accounts}
 * (io.qtek.libv2.apis.CompanyApi). Only the commonly consumed fields are typed;
 * widen as needed.
 */
export interface Company {
  _id?: string;
  companyName?: string;
  logoImage?: string;
  url?: string;
  email?: string;
}

/**
 * Service-type bitmask carried by {@link Item.productType} (unchanged from V1
 * `prTp`; the backend `productType` is a raw integer over the same
 * `io.qtek.core.apis.ServiceType` bit values).
 */
export type ItemTypeBitMask =
  | 0
  | 1
  | 2
  | 4
  | 8
  | 16
  | 32
  | 64
  | 128
  | 512
  | 1024
  | 65536;

export const BOOKING: ItemTypeBitMask = 1;
export const PRODUCT_FOR_SALE: ItemTypeBitMask = 2;
export const PRODUCT_FOR_PICKUP: ItemTypeBitMask = 4;
export const PRODUCT_FOR_DELIVERY: ItemTypeBitMask = 8;
export const PRODUCT_FOR_CATERING: ItemTypeBitMask = 16;
export const GIFT_CARD: ItemTypeBitMask = 32;
export const NFT: ItemTypeBitMask = 64;
export const OBS: ItemTypeBitMask = 128;
export const LOAN: ItemTypeBitMask = 512;
export const PRODUCT_FOR_SYN: ItemTypeBitMask = 1024;
export const ADVERTISEMENT: ItemTypeBitMask = 65536;

export const ItemTypeBitMasks = {
  BOOKING,
  PRODUCT_FOR_SALE,
  PRODUCT_FOR_PICKUP,
  PRODUCT_FOR_DELIVERY,
  PRODUCT_FOR_CATERING,
  GIFT_CARD,
  NFT,
  OBS,
  LOAN,
  PRODUCT_FOR_SYN,
  ADVERTISEMENT,
} as const;

export type ItemSummary = {
  groupName: string;
  parInd: number;
  parAlc: number;
  parCmt: number;
  oAmt: number;
  parAvlSale: number;
};
